import { Injectable } from '@angular/core';
import { FeatureFlags, ViewItems } from '@inyova/models';
import { TranslateService } from '@ngx-translate/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, filter, map, mergeMap, of, tap } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import * as RootActions from '@app/app.actions';
import * as fromAccount from '@account/account.reducers';
import * as fromRoot from '@app/app.reducers';

import { YovaAPI } from '@shared/yova-api';

import { LocalStorageKeys } from './app.constants';

@Injectable()
export class RootEffects {
  setSelectedAccountLocalStorage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RootActions.setSelectedAccount),
        map(({ account }) => {
          localStorage.setItem(LocalStorageKeys.CURRENT_ACCOUNT_ID, account.id);
        })
      ),
    { dispatch: false }
  );

  fetchFundBreakdownForAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RootActions.fetchFundBreakdownForAccount),
      mergeMap(({ accountID }) =>
        this.yovaAPI.dashboard.getFundBreakdown(accountID).pipe(
          map((res) =>
            RootActions.fetchFundBreakdownForAccountSuccess({
              accountID,
              stocks: res.stocks,
              cash: res.cash,
              language: this.translateService.currentLang
            })
          ),
          catchError(() => of(RootActions.fetchFundBreakdownForAccountFail(accountID)))
        )
      )
    )
  );

  getFundBreakdowns = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RootActions.getFundBreakdowns),
        concatLatestFrom(() => [this.store.select(fromAccount.selectCustomer), this.store.select(fromRoot.selectAccountToFundBreakdown)]),
        tap(([, customer, accountToFundBreakdown]) => {
          customer.accounts.forEach((account) => {
            if (!account.enabled_features.includes(FeatureFlags.EVE2)) {
              return;
            }
            const breakdown = accountToFundBreakdown[account.id];

            if (!breakdown || (breakdown.status !== 'LOADED' && breakdown.status !== 'LOADING')) {
              this.store.dispatch(RootActions.fetchFundBreakdownForAccount({ accountID: account.id }));
            }
          });
        })
      ),
    { dispatch: false }
  );

  updateFundBreakdownsOnLanguageChange$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RootActions.updateFundBreakdownsOnLanguageChange),
        concatLatestFrom(() => [
          this.store.select(fromAccount.selectCustomer),
          this.store.select(fromRoot.selectFundBreakdownLoadedLanguage),
          this.store.select(fromRoot.selectAccountToFundBreakdown)
        ]),
        filter(([, customer, loadedStocksLanguage]) => loadedStocksLanguage !== null && loadedStocksLanguage !== customer.language),
        tap(([, customer, , accountToFundBreakdown]) => {
          customer.accounts.forEach((account) => {
            if (!account.enabled_features.includes(FeatureFlags.EVE2)) {
              return;
            }
            const breakdown = accountToFundBreakdown[account.id];

            if (!breakdown || breakdown.status !== 'LOADING') {
              this.store.dispatch(RootActions.fetchFundBreakdownForAccount({ accountID: account.id }));
            }
          });
        })
      ),
    { dispatch: false }
  );

  getViewedItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RootActions.getViewedItems),
      concatLatestFrom(() => this.store.select(fromRoot.selectViewedItemsLoadState)),
      filter(([, loadState]) => loadState !== 'LOADED'),
      switchMap(() =>
        this.yovaAPI.dashboard.getViewedItems(Object.values(ViewItems).join(',')).pipe(
          map((viewedItemsList) => RootActions.getViewedItemsSuccess(viewedItemsList)),
          catchError((err) => of(RootActions.getViewedItemsFail()))
        )
      )
    )
  );

  setItemAsViewed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RootActions.setItemAsViewed),
      switchMap(({ item }) =>
        this.yovaAPI.dashboard.setItemAsViewed(item).pipe(
          map(() => RootActions.setItemAsViewedSuccess(item)),
          catchError(() => of(RootActions.setItemAsViewedFail()))
        )
      )
    )
  );

  unsetItemAsViewed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RootActions.unsetItemAsViewed),
      switchMap(({ item }) =>
        this.yovaAPI.dashboard.unsetItemAsViewed(item).pipe(
          map(() => RootActions.unsetItemAsViewedSuccess(item)),
          catchError(() => of(RootActions.unsetItemAsViewedFail()))
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private translateService: TranslateService,
    private yovaAPI: YovaAPI
  ) {}
}
